import type { UpdateCustomerRequestDTO } from "@/gen/api"
import { councilCustomerApi } from "@/lib/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export type UseUpdateCustomerParams = {
  onSuccess?: () => void
  onError?: (message: string) => void
}

const useUpdateCustomer = (options?: UseUpdateCustomerParams) => {
  const queryClient = useQueryClient()

  return useMutation({
    ...(options?.onError
      ? {
          onError: (error: unknown) => {
            if (error instanceof Error) {
              options.onError?.(error.message)
              return
            }
            options.onError?.("Failed to update customer")
          },
        }
      : {}),
    mutationFn: async ({
      councilId,
      customerId,
      req,
    }: {
      councilId: string
      customerId: string
      req: UpdateCustomerRequestDTO
    }) => {
      try {
        const response = await councilCustomerApi.updateCouncilCustomer(councilId, customerId, req)
        return response.data
      } catch (error: unknown) {
        if (error && typeof error === "object" && "response" in error) {
          const err = error as { response?: { data?: { message?: string } } }
          if (err.response?.data?.message) {
            throw new Error(err.response.data.message)
          }
        }
        throw error
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] })
      queryClient.invalidateQueries({
        queryKey: ["customerDetail", variables.councilId, variables.customerId],
      })
      options?.onSuccess?.()
    },
  })
}

export default useUpdateCustomer
